

import { useState } from "react";
import {
  IconArmchair,
  IconCalendar,
  IconLayoutSidebarRightCollapse,
  IconMail,
  IconMessage,
  IconPaperclip,
  IconUsersPlus,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { DocumentationTimeline } from "./components/DocumentationTimeline";
import { NoteEntry } from "./components/NoteEntry";
import { CompanionsList } from "./components/CompanionsList";
import { AppointmentsAndRequestsTab } from "./components/AppointmentsAndRequestsTab";
import { SentMessagesPanel } from "./components/SentMessagesPanel";
import { SeatingInfo } from "./components/SeatingInfo";
import { GuestAttachmentsList } from "./components/GuestAttachmentsList";
import { Guest } from "@/types/Guest";
import { useRole } from "@/providers/RoleProvider";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useEventContext } from "@/providers/EventProvider";
import { Plugin } from "@/types/enums/Plugin";
import { useTranslations } from "next-intl";
import type { Icon } from "@tabler/icons-react";
import { useMemo } from "react";
import { useResolvedGuestDetailLayout } from "../layout/useResolvedGuestDetailLayout";
import {
  SIDEBAR_TAB_KEYS,
  resolveSectionConfig,
} from "../layout/guestDetailLayout";

type TabId = "appointments" | "sentMails" | "companions" | "doku" | "seating" | "attachments";

type GuestNotesProps = {
  companion: boolean;
  guestId: string;
  firstName?: string;
  lastName?: string;
  openingId: string | Guest;
  setOpeningId: React.Dispatch<React.SetStateAction<string | Guest>>;
  /** Enables the team-shared tab order/visibility from the event's
   *  guestDetailLayout (configured in the guest-layout builder). */
  eventId?: string;
  /**
   * Is the HOST archived? Archiving a main guest cascades `archived` to its
   * companions, and both party scopes filter those out — so without this the
   * companions tab shows "no companions" for a full party, and every reader of
   * that list (the archive/unarchive confirmation counts them) reads zero.
   */
  hostArchived?: boolean;
};

export const GuestNotes: React.FC<GuestNotesProps> = ({
  companion,
  hostArchived,
  guestId,
  firstName,
  lastName,
  openingId,
  setOpeningId,
  eventId,
}) => {
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const t = useTranslations("guestDetails");
  const { legacyRoleFloor, role } = useRole();
  const { can } = useCapabilities();
  const { event } = useEventContext();
  const hasSeatingPlugin = event?.plugins?.includes(Plugin.SEATING);
  const hasAttachmentsPlugin = event?.plugins?.includes(Plugin.ATTACHMENTS);

  // Tab order + hidden set from the SELECTED layout (view-linked /
  // user-picked / Standard — same resolution as the field groups, so the
  // whole popup always follows ONE layout). The plugin/capability `show`
  // gates below stay the FINAL authority — the config can only reorder and
  // reduce, never force-show a gated tab.
  const { layout: selectedLayout, isPending: layoutPending } =
    useResolvedGuestDetailLayout(eventId);
  const sidebarConfig = useMemo(
    () => resolveSectionConfig(selectedLayout?.sidebar, SIDEBAR_TAB_KEYS),
    [selectedLayout?.sidebar],
  );

  // Each tab maps to a backend resource. A tab is only rendered when the role
  // holds the matching capability (and, where relevant, the plugin is enabled)
  // — otherwise the panel would just 403 with "noAccessToResource". The icon
  // AND its content are gated so a restricted role never sees the tab at all.
  const tabs: { id: TabId; icon: Icon; title: string; show: boolean }[] = [
    { id: "appointments", icon: IconCalendar, title: t("appointmentsTab"), show: can("slotting:read") },
    { id: "sentMails", icon: IconMail, title: t("sentMailsTab"), show: can("sent-emails:read") },
    { id: "companions", icon: IconUsersPlus, title: t("companion"), show: !companion && can("guests:read.companions") },
    { id: "doku", icon: IconMessage, title: t("notes"), show: can("guests:read") },
    { id: "seating", icon: IconArmchair, title: t("seatingTab"), show: !!hasSeatingPlugin && can("seating:read") },
    { id: "attachments", icon: IconPaperclip, title: t("attachmentsTab"), show: !!hasAttachmentsPlugin && can("attachments:read") },
  ];
  const availableTabs = sidebarConfig.order
    .map((id) => tabs.find((tab) => tab.id === id))
    .filter((tab): tab is (typeof tabs)[number] => !!tab)
    .filter((tab) => tab.show && !sidebarConfig.hidden.has(tab.id));

  // Active tab defaults to the FIRST configured tab; it also may be gated
  // away for this role (or hidden by a config change since the click) — fall
  // back to the first available tab so the panel never renders a hidden (and
  // unauthorised) tab's content.
  const activeTabId =
    activeTab && availableTabs.some((tab) => tab.id === activeTab)
      ? activeTab
      : availableTabs[0]?.id;

  // Don't paint the DEFAULT order while the config is still in flight — the
  // rail would visibly reshuffle (and config-hidden tabs flash) when it lands.
  if (eventId && layoutPending) return null;

  // Every tab hidden by config → the whole column disappears (the layout
  // builder's "no right bar" mode).
  if (availableTabs.length === 0) return null;

  const handleTabChange = (tabId: TabId) => {
    if (tabId === activeTabId) return;
    setActiveTab(tabId);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <motion.div
      className={`
        ${openingId ? "pointer-events-none opacity-30" : ""}
        shrink-0 grow md:grow-0 md:min-h-0 flex flex-col md:flex-row surface-primary rounded-2xl border outline-quaternary overflow-hidden ${isCollapsed ? "md:w-12.5" : "w-full md:w-90"}`}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Below md the rail is a horizontal, LABELLED strip across the top —
          a 48px vertical column of unnamed glyphs costs width the pane cannot
          spare on a phone, and there is no hover to reveal what they mean.
          Same recipe as ItemDetailRail in the program manager. */}
      <div
        className={`flex shrink-0 flex-row md:flex-col w-full md:w-12 min-h-0 surface-card md:rounded-l-2xl ${
          isCollapsed ? "md:rounded-r-2xl" : "border-b md:border-b-0 md:border-r outline-quaternary"
        }`}
        key={`${isCollapsed}-${activeTab}`}
      >
        {/* Scrollable icon area — only tabs the role may access are rendered */}
        <div className="flex min-h-0 flex-1 shrink-0 flex-row gap-1 overflow-x-auto p-2 md:flex-col md:space-y-1 md:gap-0 md:overflow-x-visible md:overflow-y-auto md:px-2 md:pt-2 md:pb-0 md:items-center">
          {availableTabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex shrink-0 items-center gap-1.5 p-2.5 rounded-lg ${
                  activeTabId === tab.id ? "font-light-primary surface-dark" : ""
                } `}
                onClick={() => handleTabChange(tab.id)}
                title={tab.title}
              >
                <TabIcon size={20} className="shrink-0" />
                {/* ONE label element: visible in the phone strip, and
                    screen-reader-only once the rail becomes an icon column.
                    Not `hidden md:sr-only` — below md `display:none` would beat
                    sr-only and the name would be announced at no width. */}
                <span className="body-4-regular md:sr-only">{tab.title}</span>
              </button>
            );
          })}
        </div>

        {/* Pinned collapse button — meaningless in the horizontal strip, where
            the pane is already full-width. */}
        <div className="hidden md:flex shrink-0 justify-center px-2 pb-3 pt-1">
          <button
            key={`button-${isCollapsed}`}
            onClick={toggleCollapse}
            className="p-1 hover:surface-secondary rounded-full transition-colors"
            aria-label={isCollapsed ? t("expandSidebar") : t("collapseSidebar")}
          >
            <motion.div
              animate={{ rotate: isCollapsed ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <IconLayoutSidebarRightCollapse size={20} />
            </motion.div>
          </button>
        </div>
      </div>

      {/* Content based on active tab */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            className="px-3 pb-1 grow overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTabId === "appointments" ? (
              <AppointmentsAndRequestsTab
                firstName={firstName || ""}
                lastName={lastName || ""}
                guestId={guestId}
              />
            ) : activeTabId === "sentMails" ? (
              // SMS channel wave: the mail list, plus the SMS history behind
              // a switch while the channel is on.
              <SentMessagesPanel guestId={guestId} />
            ) : activeTabId === "companions" ? (
              <CompanionsList
                guestId={guestId}
                setOpeningId={setOpeningId}
                hostArchived={hostArchived}
              />
            ) : activeTabId === "doku" ? (
              <div className="flex h-full flex-col">
                <DocumentationTimeline guestId={guestId} />
                {legacyRoleFloor !== "VIEWER" && can("guests:write") && (
                  <NoteEntry guestId={guestId} />
                )}
              </div>
            ) : activeTabId === "seating" ? (
              <SeatingInfo guestId={guestId} />
            ) : activeTabId === "attachments" ? (
              <GuestAttachmentsList guestId={guestId} />
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
